import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { graphClient } from "helpers/api";
import endpoints from "helpers/endpoints";
import ADUser from "types/ADUser.type";
import UserDetails from "types/UserDetails.type";
import * as constants from "helpers/constants";

const fetchUser = async () => {
  return api({
    url: endpoints.Me
  });
};

const useUser = (isAuthenticated: boolean = true) => {
  const result = useQuery({
    queryKey: ["user-details"],
    queryFn: fetchUser,
    select: (data): UserDetails => data.data.user,
    enabled: isAuthenticated,
    staleTime: constants.USERS_API_STALE_TIME, // 1 hour
    meta: {
      errorMessage: "E.USER-002"
    }
  });
  return { ...result, user: result.data };
};

const addNewUser = (data) => {
  return api({
    url: endpoints.Users,
    method: "post",
    data
  });
};

const useAddNewUser = (onUserAdded) => {
  return useMutation({
    mutationFn: addNewUser,
    onSettled: onUserAdded,
    meta: {
      errorMessage: "E.USER-003"
    }
  });
};

const useUpdateUserRoles = (onRoleUpdated) => {
  return useMutation({
    mutationFn: (data: { userId: number; roleId: number[] }) => {
      return api({
        url: endpoints.UpdateRoles(data.userId),
        method: "put",
        data: data.roleId,
        headers: { "Content-Type": "application/json" }
      });
    },
    onSettled: onRoleUpdated,
    meta: {
      errorMessage: "E.USER-004"
    }
  });
};

const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, active }: { userId: number; active: boolean }) => {
      return api({
        url: `api/Users/${userId}`,
        method: "put",
        data: active,
        headers: { "Content-Type": "application/json" }
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["all-portal-users"] });
    },
    meta: {
      errorMessage: "E.USER-004"
    }
  });
};

const useUserProfileImage = (isAuthenticated) => {
  const result = useQuery({
    queryKey: ["profile-image"],
    queryFn: async () => {
      return graphClient.api(endpoints.ProfileImage).get();
    },
    enabled: isAuthenticated,
    cacheTime: Infinity,
    staleTime: Infinity
  });

  let profileImage = "";
  if (result.isSuccess) {
    profileImage = URL.createObjectURL(result.data);
  }

  return { ...result, profileImage };
};

const fetchADUsers = async ({ queryKey }) => {
  const searchText = queryKey[1];

  return api({
    url: endpoints.UserAD,
    params: {
      searchText
    }
  });
};
const useUserSearch = (searchText, onSearchSuccess) => {
  return useQuery({
    queryKey: ["user-search", searchText],
    queryFn: fetchADUsers,
    select: (data): ADUser[] => {
      if (data.data) {
        return data.data.userDetails.filter((user) => !!user.mail).slice(0, 5);
      } else {
        return [];
      }
    },
    enabled: Boolean(searchText),
    onSuccess: onSearchSuccess,
    meta: {
      errorMessage: "E.USER-001"
    }
  });
};

const useAllUsers = () => {
  return useQuery({
    queryKey: ["all-portal-users"],
    queryFn: () => {
      return api({
        url: `api/Users`,
        method: "get"
      });
    },
    select: (data): UserDetails[] => data.data.userDetails,
    meta: {
      errorMessage: "E.USER-005"
    }
  });
};

const useUserLogout = (onLogoutSettled) => {
  return useMutation({
    mutationKey: ["logout", "audit"],
    mutationFn: () => {
      return api({ url: endpoints.UserLogout, method: "post" });
    },
    onSettled: onLogoutSettled
  });
};

export {
  useUser,
  useAddNewUser,
  useUpdateUserStatus,
  useUpdateUserRoles,
  useUserProfileImage,
  useUserSearch,
  useAllUsers,
  useUserLogout
};
